import { ActionDto, ApiDataItemDto } from './Action.dto';
import { ActionTypeEnum } from '@/Domain/enum/ActionType.enum';

export type ConnectRemoteMcpDto = {
  remote_mcp_url: string;
  headers?: ApiDataItemDto[];
  // Optional metadata from a catalog pick, carried into the create payload.
  name?: string;
  description?: string;
  icon?: string;
  icon_color?: string;
};

export type RemoteMcpToolNames = {
  tool_names: string[];
};

/** Response of the connect endpoint: tool_names when reachable, or requires_oauth
 * with the discovered provider details when the server needs OAuth. */
export type RemoteMcpConnectResult = {
  tool_names: string[];
  requires_oauth: boolean;
  authorization_server: string | null;
  resource: string | null;
  provider_label: string | null;
};

export const isRemoteMcpOAuthRequired = (result: RemoteMcpConnectResult): boolean =>
  result.requires_oauth === true;

export type RemoteMcpOAuthFailureReason =
  | 'access_denied'
  | 'browser_mismatch'
  | 'finalize_failed'
  | 'provider_error'
  | 'token_exchange_failed';

export type RemoteMcpOAuthStatus = {
  status: 'needs_authorization' | 'finalizing' | 'authorized' | 'error';
  provider_label: string;
  authorization_in_progress?: boolean;
  failure_reason?: RemoteMcpOAuthFailureReason | null;
};

export type RemoteMcpOAuthStartDto = {
  action_id: number;
};

export type RemoteMcpOAuthStartResult = {
  authorization_url: string;
  provider_label: string;
};

export type RemoteMcpOAuthResultMessage = {
  type: 'remote_mcp_oauth_result';
  status: 'success' | 'error';
  action_id: number | null;
  state: string | null;
  reason_code?: string;
};

export type RemoteMcpToolMemoryCapture = {
  json_path: string;
  metadata_key: string;
};

export type RemoteMcpRecommendedMemory = {
  tool_name: string;
  save_to_memory: RemoteMcpToolMemoryCapture[];
};

export type RemoteMcpRecommendedSetup = {
  tools: string[];
  memory: RemoteMcpRecommendedMemory[];
  summary_key: string;
};

export type RemoteMcpTool = {
  name: string;
  is_active: boolean;
  // Optional per-tool override for the widget "thinking" message; null/absent -> derived from name.
  thinking_message?: string | null;
  // Values to lift out of the tool's result into conversation memory; absent for tools that capture nothing.
  save_to_memory?: RemoteMcpToolMemoryCapture[] | null;
};

export type RemoteMcpCreateDto = ConnectRemoteMcpDto & {
  allowed_tools: RemoteMcpTool[];
};

export type RemoteMcpDto = ActionDto & {
  type: ActionTypeEnum.RemoteMcp;
  remote_mcp_url: string;
  remote_mcp_auth_token: string;
  default_tool_is_active: boolean;
  headers: ApiDataItemDto[];
  allowed_tools: RemoteMcpTool[];
  oauth?: RemoteMcpOAuthStatus | null;
  // Read-only preview of what "Use recommended" applies; absent for servers Quickchat has not curated.
  recommended_setup?: RemoteMcpRecommendedSetup | null;
  // Deprecated duplicate of recommended_setup.tools; the only shape merge-base pods emit.
  recommended_tools?: string[];
  // Read-only: the message each tool shows the visitor when its own field is left empty.
  thinking_message_defaults?: Record<string, string>;
};

/** The setup to preview, synthesized tools-only when a merge-base pod sent just the legacy field. */
export const recommendedSetupOf = (
  action?: RemoteMcpDto | null,
): RemoteMcpRecommendedSetup | null =>
  action?.recommended_setup ??
  (action?.recommended_tools?.length
    ? { tools: action.recommended_tools, memory: [], summary_key: '' }
    : null);

export type RemoteMcpUpdateDto = {
  id: number;
  remote_mcp_url?: string;
  headers?: ApiDataItemDto[];
  default_tool_is_active?: boolean;
  allowed_tools?: RemoteMcpTool[];
  name?: string;
  description?: string;
  clear_oauth_credentials?: boolean;
};
